import type { IconButtonProps } from '@mui/material/IconButton';

import { varAlpha } from 'minimal-shared/utils';
import { useState, useEffect, useCallback } from 'react';
import { useBoolean, usePopover } from 'minimal-shared/hooks';

import MenuItem from '@mui/material/MenuItem';
import {
  Box,
  Badge,
  Avatar,
  Tooltip,
  useTheme,
  Typography,
  IconButton,
  ListItemText,
} from '@mui/material';

import { useRouter } from 'src/routes/hooks';

import { removeTenantIdLS } from 'src/utils/localStorageUtils';

import { useTranslate } from 'src/locales';
import { type RootState } from 'src/redux/store';
import { useAppDispatch, useAppSelector } from 'src/hook/hooks';
import { truncateText } from 'src/pages/chat/utils/get-message';
import { useLogoutUserMutation } from 'src/pages/auth/slice/authApi';
import { setAuthStateInitial } from 'src/pages/auth/slice/authSlice';
import { setChatStateInitial } from 'src/pages/chat/slice/chatSlice';
import { useGetMediaUrlMutation } from 'src/pages/chat/slice/chatApi';
import { useCallContext } from 'src/pages/chat/hooks/use-call-context';
import { resetSettingState } from 'src/pages/setting/slice/settingSlice';
import ChangePasswordForm from 'src/pages/auth/component/changePasswordForm';
import { setChannelStateInitial } from 'src/pages/channel/slice/channelSlice';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { CustomPopover } from 'src/components/custom-popover';

import { useAuthContext } from 'src/auth/hooks';
import { signOut } from 'src/auth/context/jwt/action';

import { BuyNowButton } from './buynow-button';
import { AccountButton } from './account-button';
import { SignOutButton } from './sign-out-button';
// ----------------------------------------------------------------------

export type AccountDrawerProps = IconButtonProps & {
  data?: {
    id: string;
    label: string;
    href: string;
    icon?: React.ReactNode;
    info?: React.ReactNode;
  }[];
};

export function AccountDrawer({ data = [], sx, ...other }: AccountDrawerProps) {
  const theme = useTheme();
  const { t } = useTranslate('translation');
  const { reconnectSIP, registered, isConnecting, handleResetCallDetails, disconnectSIP } =
    useCallContext();

  const { loginApiData } = useAppSelector(
    (state: RootState & { auth: RootState['auth'] }) => state.auth
  );

  const { checkUserSession } = useAuthContext();

  const dispatch = useAppDispatch();
  const { open, anchorEl, onClose, onOpen } = usePopover();

  const [logoutUser, { data: logoutApiResp, isLoading: logoutApiLoader, error: logoutApiError }] =
    useLogoutUserMutation();
  const [getMediaUrl] = useGetMediaUrlMutation();

  const [selectedContactId, setSelectedContactId] = useState<string[] | null>(null);
  const [profileImgUrl, setProfileImgUrl] = useState('');
  const changePassWordMenu = useBoolean();
  const router = useRouter();

  const handleSelectContact = (id: string) => {
    if (id === 'CHANGE_PASSWORD') {
      changePassWordMenu.onTrue();
      onClose();
      return;
    }
    router.replace(id);
    onClose();
  };

  const signOutHandler = async () => {
    try {
      await logoutUser({ email: loginApiData?.email }).unwrap();
    } catch (error) {
      console.error(error);
    }
  };

  const clearState = useCallback(async () => {
    await signOut(); // Reset Session
    dispatch(setAuthStateInitial());
    dispatch(setChatStateInitial());
    dispatch(setChannelStateInitial());
    dispatch(resetSettingState());
    handleResetCallDetails();
    removeTenantIdLS();
    // persistor.purge();
    await checkUserSession?.();
  }, []);

  useEffect(() => {
    if (!logoutApiResp) return;

    if (logoutApiResp.success && logoutApiResp.message === 'Logout successful') {
      onClose();
      clearState();
      disconnectSIP();
    }
  }, [logoutApiResp]);

  useEffect(() => {
    if (!logoutApiError) return;

    if ('status' in logoutApiError && logoutApiError.status === 'FETCH_ERROR') {
      toast.error(t('SOMETHING_WENT_WRONG'));
    }
  }, [logoutApiError]);

  useEffect(() => {
    const getImgUrl = async () => {
      const filePath = loginApiData && 'ProfileURL' in loginApiData && loginApiData.ProfileURL;
      if (!filePath) return;

      const { data: uploadFileUrl } = await getMediaUrl({ fileName: filePath || '' });

      if (uploadFileUrl) {
        setProfileImgUrl(uploadFileUrl);
      }
    };

    if (loginApiData && 'ProfileURL' in loginApiData && loginApiData?.ProfileURL) {
      getImgUrl();
    }
  }, [loginApiData]);

  const renderLine = () => (
    <hr
      style={{
        width: '100%',
        border: `dashed 1px ${varAlpha(theme.vars.palette.grey['500Channel'], 0.32)}`,
      }}
    />
  );

  const renderMenuList = () => (
    <CustomPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      slotProps={{ arrow: { offset: 20 } }}
    >
      <Scrollbar sx={{ height: 'auto', width: 280 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 1,
          }}
        >
          <Box>
            {/* <Badge
              sx={{ position: 'relative' }}
              badgeContent=""
              variant={registered ? 'online' : 'offline'}
            > */}
            <Avatar
              alt={
                loginApiData && 'firstName' in loginApiData && 'lastName' in loginApiData
                  ? loginApiData.firstName || loginApiData?.lastName
                  : ''
              }
              src={
                profileImgUrl ||
                (loginApiData && 'firstName' in loginApiData && loginApiData.firstName) ||
                (loginApiData && 'lastName' in loginApiData && loginApiData.lastName) ||
                ''
              }
              sx={{ width: 48, height: 48 }}
            />
            {/* </Badge> */}
          </Box>
          <Box>
            <Tooltip
              title={
                loginApiData &&
                'firstName' in loginApiData &&
                'lastName' in loginApiData &&
                `${loginApiData.firstName} ${loginApiData.lastName}`.length > 28
                  ? `${loginApiData.firstName} ${loginApiData.lastName}`
                  : ''
              }
              arrow
              placement="bottom"
            >
              <Typography variant="subtitle1" noWrap>
                {loginApiData && 'firstName' in loginApiData && 'lastName' in loginApiData
                  ? truncateText(`${loginApiData.firstName} ${loginApiData.lastName}`, 20)
                  : ''}
              </Typography>
            </Tooltip>

            {loginApiData && loginApiData?.email && (
              <Tooltip
                title={loginApiData?.email.length > 28 ? loginApiData?.email : ''}
                arrow
                placement="bottom"
              >
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', mt: 0.1, fontSize: 12, cursor: 'pointer' }}
                >
                  {truncateText(loginApiData?.email, 28)}
                </Typography>
              </Tooltip>
            )}

            {loginApiData &&
              'tenant' in loginApiData &&
              typeof loginApiData.tenant === 'object' &&
              loginApiData.tenant !== null &&
              'name' in loginApiData.tenant &&
              (loginApiData.tenant as { name?: string }).name &&
              (loginApiData.tenant as { name?: string }).name !== 'MASTER' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', mt: 0.1, fontSize: 12 }}
                  >
                    {t('WEBPHONE')}:{' '}
                    {loginApiData && 'extNumber' in loginApiData ? loginApiData.extNumber : ''}
                  </Typography>
                  <Badge
                    sx={{
                      position: 'relative',
                      '& .MuiBadge-badge': {
                        width: 8,
                        height: 8,
                      },
                    }}
                    badgeContent=""
                    variant={registered ? 'online' : 'offline'}
                  />

                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', mt: 0.1, fontSize: 12 }}
                  >
                    {!isConnecting ? (registered ? t('CONNECTED') : t('DISCONNECTED')) : ''}
                    {isConnecting && t('CONNECTING')}
                    <IconButton
                      size="small"
                      sx={{ width: 15, height: 15, ml: 0.5 }}
                      onClick={reconnectSIP}
                      disabled={isConnecting}
                    >
                      <Iconify icon="tdesign-refresh" sx={{ width: 12 }} />
                    </IconButton>
                  </Typography>
                </Box>
              )}
          </Box>
        </Box>
        {renderLine()}
        {data.map((contact) => {
          const isUserProfile = contact?.id === 'user-profile';

          const shouldShowUserProfile =
            isUserProfile &&
            loginApiData &&
            'tenant' in loginApiData &&
            typeof loginApiData.tenant === 'object' &&
            loginApiData.tenant !== null &&
            'name' in loginApiData.tenant &&
            (loginApiData.tenant as { name?: string }).name &&
            (loginApiData.tenant as { name?: string }).name !== 'MASTER';

          // Skip rendering 'user-profile' if tenant is MASTER
          if (isUserProfile && !shouldShowUserProfile) {
            return null;
          }

          return (
            <MenuItem
              key={contact.label}
              sx={{
                p: 1,
                px: 2,
                bgcolor: selectedContactId?.includes(contact.label) ? 'action.selected' : 'inherit',
              }}
              onClick={() => handleSelectContact(contact.href)}
            >
              <ListItemText
                primary={contact.label}
                slotProps={{
                  secondary: {
                    sx: { typography: 'caption', color: 'text.disabled' },
                  },
                }}
              />
            </MenuItem>
          );
        })}

        {renderLine()}
        {loginApiData &&
          'tenant' in loginApiData &&
          typeof loginApiData.tenant === 'object' &&
          loginApiData.tenant !== null &&
          'name' in loginApiData.tenant &&
          (loginApiData.tenant as { name?: string }).name &&
          (loginApiData.tenant as { name?: string }).name !== 'MASTER' && (
            <>
              <BuyNowButton handleMenuClose={() => onClose()} />
              {renderLine()}
            </>
          )}
        <SignOutButton onClose={signOutHandler} />
      </Scrollbar>
    </CustomPopover>
  );

  return (
    <>
      <AccountButton
        onClick={onOpen}
        photoURL={profileImgUrl}
        displayName={
          loginApiData && 'firstName' in loginApiData && 'lastName' in loginApiData
            ? loginApiData.firstName || loginApiData?.lastName
            : ''
        }
        registered={registered}
        sx={sx}
        {...other}
      />
      <ChangePasswordForm
        open={changePassWordMenu.value}
        onCloseHandler={() => changePassWordMenu.onFalse()}
      />
      {renderMenuList()}
    </>
  );
}
